import { useState, useEffect } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useAuth } from './useAuth';

// Simple retry mechanism with exponential backoff
const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Don't retry on certain error types
      if (error.code === 'PGRST116' || error.message?.includes('row-level security')) {
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`Retry attempt ${attempt + 1}/${maxRetries + 1} after ${delay}ms delay`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
};

export interface SubscriptionStatus {
  isTrialActive: boolean;
  trialEndsAt: Date | null;
  isSubscribed: boolean;
  isLifetime: boolean;
  daysLeftInTrial: number | null;
  subscription: {
    id: string | null;
    price_id: string | null;
    product_id: string | null;
    subscription_status: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    duration_months: number;
  } | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const useSubscriptionStatus = (): SubscriptionStatus => {
  const { supabase, testConnection } = useSupabase();
  const { user } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus>({
    isTrialActive: false,
    trialEndsAt: null,
    isSubscribed: false,
    isLifetime: false,
    daysLeftInTrial: null,
    subscription: null,
    loading: true,
    error: null,
    refresh: async () => {}
  });

  const checkStatus = async () => {
    if (!user) {
      setStatus(prev => ({ 
        ...prev, 
        loading: false,
        error: null 
      }));
      return;
    }

    try {
      const connectionOk = await testConnection();

      if (!connectionOk) {
        throw new Error('Não foi possível conectar ao Supabase. Verifique sua conexão com a internet e as configurações do projeto.');
      }

      // Buscar dados do perfil com timeout
      const { data: profileData, error: profileError } = await retryWithBackoff(
        async () => {
          const profilePromise = supabase
            .from('profiles')
            .select('trial_ends_at, lifetime, role, email, plano_atual, plano_expira_em, checkout_session_id, stripe_customer_id')
            .eq('id', user.id)
            .single();

          // Adicionar timeout de 10 segundos
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Timeout: A consulta demorou muito para responder')), 10000);
          });

          return await Promise.race([profilePromise, timeoutPromise]) as any;
        },
        3, // 3 retry attempts
        1000 // 1 second base delay
      );

      if (profileError) {
        if (profileError.code === 'PGRST116' || profileError.message?.includes('row-level security')) {
          // Continuar com valores padrão
        } else {
          throw profileError;
        }
      }

      const isLifetime = profileData?.lifetime === true || 
                         profileData?.role === 'admin' || 
                         profileData?.email === 'rica@agsupernova.com';

      let isTrialActive = false;
      let daysLeftInTrial = null;
      let trialEndsAt = null;

      if (profileData?.trial_ends_at && !isLifetime) {
        const tempTrialDate = new Date(profileData.trial_ends_at);
        if (!isNaN(tempTrialDate.getTime())) {
          trialEndsAt = tempTrialDate;
          const now = new Date();
          if (trialEndsAt > now) {
            isTrialActive = true;
            daysLeftInTrial = Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
          }
        } else {
          console.warn('Invalid trial_ends_at date:', profileData.trial_ends_at);
        }
      }

      let subscriptionData = null;
      let subscriptionError = null;

      // Tentar buscar dados de assinatura com tratamento de erro melhorado
      try {
        // Primeiro verificar se temos um customer_id no perfil
        if (profileData?.stripe_customer_id) {
          const result = await retryWithBackoff(
            async () => {
              return await supabase
                .from('stripe_subscriptions')
                .select(`
                  subscription_id,
                  price_id,
                  status,
                  current_period_end,
                  cancel_at_period_end
                `)
                .eq('customer_id', profileData.stripe_customer_id)
                .eq('status', 'active')
                .maybeSingle();
            },
            2, // 2 retry attempts for subscription data
            500 // 500ms base delay
          ) as any;

          if (result?.data) {
            subscriptionData = result.data;
          }
        }
        
        // Se não encontrou via perfil, buscar via tabela de customers
        if (!subscriptionData) {
          const result1 = await retryWithBackoff(
            async () => {
              return await supabase
                .from('stripe_subscriptions')
                .select(`
                  subscription_id,
                  price_id,
                  status,
                  current_period_end,
                  cancel_at_period_end,
                  stripe_customers!inner(user_id)
                `)
                .eq('stripe_customers.user_id', user.id)
                .eq('status', 'active')
                .maybeSingle();
            },
            2,
            500
          ) as any;

          if (result1?.data) {
            subscriptionData = result1.data;
          } else if (result1?.error) {
            const result2 = await retryWithBackoff(
              async () => {
                return await supabase
                  .from('stripe_subscriptions')
                  .select(`
                    subscription_id,
                    price_id,
                    status,
                    current_period_end,
                    cancel_at_period_end,
                    stripe_customers!inner(user_id)
                  `)
                  .eq('stripe_customers.user_id', user.id)
                  .order('created_at', { ascending: false })
                  .limit(1);
              },
              2,
              500
            ) as any;

            subscriptionData = result2?.data?.[0];
            subscriptionError = result2?.error;
          }
        }
      } catch (subError: any) {
        subscriptionError = subError;
      }

      let hasActivePlan = false;
      if (profileData?.plano_atual && profileData?.plano_expira_em) {
        const planExpirationDate = new Date(profileData.plano_expira_em);
        if (!isNaN(planExpirationDate.getTime())) {
          hasActivePlan = planExpirationDate > new Date();
        }
      }

      let isSubscribed = 
        isLifetime || 
        hasActivePlan ||
        subscriptionData?.status === 'active' || 
        subscriptionData?.status === 'trialing';

      let durationMonths = 1;
      let priceId = subscriptionData?.price_id;

      if (hasActivePlan && !subscriptionData?.price_id) {
        const planMap: Record<string, string> = {
          'Premium': 'price_1RIKr4B0B3nriuwYoFRXi3rH',
          'Profissional': 'price_1RIKoZB0B3nriuwYp6w76psa',
          'Básico': 'price_1RIIzxB0B3nriuwYPbCODlbo'
        };
        priceId = planMap[profileData.plano_atual] || null;
      }

      if (priceId) {
        if (priceId === 'price_1RtW0mB0B3nriuwY1nJcHogh') {
          durationMonths = 12;
        } else if (priceId === 'price_1RtW7UB0B3nriuwYofTFEbYJ') {
          durationMonths = 12;
        } else {
          durationMonths = 1;
        }
      }

      // ✅ TRATAMENTO SEGURO DO current_period_end
      let currentPeriodEnd: string | null = null;

      if (isLifetime) {
        currentPeriodEnd = '2125-01-01T00:00:00.000Z';
      } else if (subscriptionData?.current_period_end) {
        const timestamp = typeof subscriptionData.current_period_end === 'number'
          ? subscriptionData.current_period_end * 1000
          : Date.parse(subscriptionData.current_period_end);

        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          currentPeriodEnd = date.toISOString();
        } else {
          console.warn('Data inválida em current_period_end:', subscriptionData.current_period_end);
        }
      } else if (profileData?.plano_expira_em) {
        const planExpirationDate = new Date(profileData.plano_expira_em);
        if (!isNaN(planExpirationDate.getTime())) {
          currentPeriodEnd = planExpirationDate.toISOString();
        } else {
          console.warn('Data inválida em plano_expira_em:', profileData.plano_expira_em);
        }
      }

      setStatus({
        isTrialActive,
        trialEndsAt,
        isSubscribed: isSubscribed || isLifetime,
        isLifetime,
        daysLeftInTrial,
        subscription: isSubscribed && (subscriptionData || hasActivePlan) ? {
          id: subscriptionData?.subscription_id || 'profile_subscription',
          price_id: priceId,
          product_id: null,
          subscription_status: subscriptionData?.status || 'active',
          current_period_end: currentPeriodEnd,
          cancel_at_period_end: subscriptionData?.cancel_at_period_end || false,
          duration_months: durationMonths
        } : isLifetime ? {
          id: 'lifetime',
          price_id: 'price_lifetime',
          product_id: 'prod_lifetime',
          subscription_status: 'active',
          current_period_end: currentPeriodEnd,
          cancel_at_period_end: false,
          duration_months: 999
        } : null,
        loading: false,
        error: null,
        refresh: checkStatus
      });

    } catch (error: any) {
      console.error('Erro ao verificar assinatura:', error);
      
      let errorMessage = 'Erro ao verificar status da assinatura';
      
      if (error.message?.includes('fetch') || error.name === 'TypeError') {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (error.message?.includes('Timeout')) {
        errorMessage = 'A consulta demorou muito para responder. Tente novamente.';
      } else if (error.message?.includes('Supabase')) {
        errorMessage = error.message;
      } else if (error.code === 'PGRST116') {
        errorMessage = 'Erro de permissão. Entre em contato com o suporte.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setStatus(prev => ({
        ...prev,
        isTrialActive: false,
        trialEndsAt: null,
        isSubscribed: false,
        isLifetime: false,
        daysLeftInTrial: null,
        subscription: null,
        loading: false,
        error: errorMessage,
        refresh: checkStatus
      }));
    }
  };

  useEffect(() => {
    checkStatus();

    // Configurar listeners para mudanças em tempo real
    const subscriptionsChannel = supabase
      .channel('stripe_subscription_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'stripe_subscriptions'
      }, () => {
        console.log('Subscription change detected, refreshing...');
        checkStatus();
      })
      .subscribe();

    const customersChannel = supabase
      .channel('stripe_customer_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'stripe_customers'
      }, () => {
        console.log('Customer change detected, refreshing...');
        checkStatus();
      })
      .subscribe();

    const profilesChannel = supabase
      .channel('profile_changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${user?.id}`
      }, () => {
        console.log('Profile change detected, refreshing...');
        checkStatus();
      })
      .subscribe();

    // Polling menos frequente para evitar sobrecarga
    const pollingInterval = setInterval(() => {
      checkStatus();
    }, 60000); // 1 minuto em vez de 30 segundos

    return () => {
      supabase.removeChannel(subscriptionsChannel);
      supabase.removeChannel(customersChannel);
      supabase.removeChannel(profilesChannel);
      clearInterval(pollingInterval);
    };
  }, [user, supabase, testConnection]);

  return status;
};

export default useSubscriptionStatus;